/**
 * Hook tải dữ liệu trả về ĐÚNG NĂM trạng thái của Bảng 3.3 đặc tả giao diện.
 *
 *   'dang-tai'   — đang truy vấn
 *   'co-du-lieu' — có dữ liệu
 *   'rong'       — rỗng vì CHƯA CÓ dữ liệu
 *   'rong-loc'   — rỗng vì BỘ LỌC
 *   'loi'        — lỗi
 *
 * Hai trạng thái rỗng được phân biệt bằng cờ `dangLoc` mà nơi gọi truyền vào:
 * kết quả rỗng khi KHÔNG có điều kiện lọc nào nghĩa là địa bàn chưa khai báo thiết
 * bị; kết quả rỗng khi CÓ điều kiện lọc nghĩa là bộ lọc không khớp. Hai tình
 * huống này cần câu chữ và hành động khác nhau nên không được gộp.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { LoiApi } from '../api/thiet-bi-api';

export type TenTrangThai = 'dang-tai' | 'co-du-lieu' | 'rong' | 'rong-loc' | 'loi';

export interface KetQuaTai<T> {
  trangThai: TenTrangThai;
  duLieu: T | null;
  loi: { ma: string; thongBao: string; requestId?: string } | null;
  /** Đã tải quá 10 giây mà chưa có dữ liệu: bổ sung dòng chữ giải thích. */
  taiLau: boolean;
  taiLai: () => void;
  /** Số lần tải xong, dùng cho kiểm thử tự làm mới. */
  soLanTai: number;
}

export interface TuyChonTai<T> {
  /** Hàm tải; nhận AbortSignal để huỷ khi bộ lọc đổi trước khi kết quả về. */
  tai: (signal: AbortSignal) => Promise<T>;
  /** Dữ liệu trả về có rỗng không. */
  laRong: (d: T) => boolean;
  /** Đang có điều kiện lọc nào không — quyết định 'rong' hay 'rong-loc'. */
  dangLoc: boolean;
  /** Đổi giá trị nào thì tải lại. */
  phuThuoc: unknown[];
  /** Tắt hoàn toàn (ví dụ chưa có quyền). */
  tat?: boolean;
}

export function dungTaiDuLieu<T>(tc: TuyChonTai<T>): KetQuaTai<T> {
  const [trangThai, datTrangThai] = useState<TenTrangThai>('dang-tai');
  const [duLieu, datDuLieu] = useState<T | null>(null);
  const [loi, datLoi] = useState<KetQuaTai<T>['loi']>(null);
  const [taiLau, datTaiLau] = useState(false);
  const [soLanTai, datSoLanTai] = useState(0);
  const [mocTaiLai, datMocTaiLai] = useState(0);
  const taiRef = useRef(tc.tai);
  const rongRef = useRef(tc.laRong);
  taiRef.current = tc.tai;
  rongRef.current = tc.laRong;

  const taiLai = useCallback(() => datMocTaiLai((x) => x + 1), []);

  useEffect(() => {
    if (tc.tat) return undefined;
    const boHuy = new AbortController();
    datTrangThai('dang-tai');
    datLoi(null);
    datTaiLau(false);
    const dongHo = setTimeout(() => datTaiLau(true), 10_000);

    void taiRef
      .current(boHuy.signal)
      .then((d) => {
        if (boHuy.signal.aborted) return;
        datDuLieu(d);
        datTrangThai(rongRef.current(d) ? (tc.dangLoc ? 'rong-loc' : 'rong') : 'co-du-lieu');
        datSoLanTai((x) => x + 1);
      })
      .catch((e: unknown) => {
        if (boHuy.signal.aborted) return;
        // Yêu cầu bị huỷ vì bộ lọc đổi thì không phải lỗi của người dùng.
        if (e instanceof DOMException && e.name === 'AbortError') return;
        const la = e instanceof LoiApi ? e : null;
        datLoi({
          ma: la?.ma ?? 'INTERNAL_ERROR',
          thongBao: la?.message ?? 'Không kết nối được tới máy chủ',
          requestId: la?.requestId,
        });
        datTrangThai('loi');
        datSoLanTai((x) => x + 1);
      })
      .finally(() => clearTimeout(dongHo));

    return () => {
      clearTimeout(dongHo);
      boHuy.abort();
    };
    // Mảng phụ thuộc do nơi gọi khai báo (`tc.phuThuoc`) nên độ dài cố định
    // trong suốt vòng đời thành phần — đúng ràng buộc của React về hook.
  }, [...tc.phuThuoc, mocTaiLai, tc.tat, tc.dangLoc]);

  return { trangThai, duLieu, loi, taiLau, taiLai, soLanTai };
}

/**
 * Hoãn giá trị: dùng cho ô tìm kiếm (400 mili giây) và cho khung nhìn bản đồ
 * (500 mili giây) theo chương 12 đặc tả giao diện.
 */
export function dungHoan<T>(giaTri: T, treMs: number): T {
  const [daHoan, datDaHoan] = useState(giaTri);
  useEffect(() => {
    const h = setTimeout(() => datDaHoan(giaTri), treMs);
    return () => clearTimeout(h);
  }, [giaTri, treMs]);
  return daHoan;
}

/**
 * Tự làm mới có điều kiện, đúng quy tắc "Làm mới" của Bảng 3.1 và Bảng 12.1:
 * chỉ chạy khi tab đang hiển thị VÀ người dùng không đang chọn dòng hay mở ngăn
 * chi tiết. Trả về lý do đang tạm dừng để giao diện nói rõ cho người dùng.
 */
export function dungTuLamMoi(
  lamMoi: () => void,
  tuyChon: { chuKyMs?: number; dangChonDong: boolean; dangMoNgan: boolean },
): { dangTamDung: boolean; lyDo: 'tab-an' | 'dang-chon' | null } {
  const chuKy = tuyChon.chuKyMs ?? 60_000;
  const [tabAn, datTabAn] = useState<boolean>(
    typeof document !== 'undefined' ? document.visibilityState === 'hidden' : false,
  );
  const lamMoiRef = useRef(lamMoi);
  lamMoiRef.current = lamMoi;

  useEffect(() => {
    const doi = (): void => datTabAn(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', doi);
    return () => document.removeEventListener('visibilitychange', doi);
  }, []);

  const nguoiDungDangLamViec = tuyChon.dangChonDong || tuyChon.dangMoNgan;
  const dangTamDung = tabAn || nguoiDungDangLamViec;

  useEffect(() => {
    if (dangTamDung) return undefined;
    const h = setInterval(() => lamMoiRef.current(), chuKy);
    return () => clearInterval(h);
  }, [dangTamDung, chuKy]);

  return {
    dangTamDung,
    lyDo: tabAn ? 'tab-an' : nguoiDungDangLamViec ? 'dang-chon' : null,
  };
}
